
import { SpotifyTrack, TrackDownload } from '@/lib/types';
import { extractSpotifyTrackId } from '@/lib/spotify';
import { validateUploadRequest } from './validationUtils';
import { uploadFileToStorage } from './fileUploadUtils';
import { saveTrackToDatabase } from './db/saveTrack';
import { useToast } from '@/components/ui/use-toast';

export async function performUpload(
  e: React.FormEvent,
  spotifyUrl: string,
  selectedFile: File | null,
  trackPreview: SpotifyTrack | null,
  validateSpotifyUrl: (url: string) => boolean,
  artistId: string | null,
  toast: ReturnType<typeof useToast>['toast'],
  setUploadError: (error: string | null) => void,
  trackTitle?: string,
  trackDescription?: string
) {
  e.preventDefault();
  setUploadError(null);
  
  console.log("Starting upload process with:", {
    hasFile: !!selectedFile,
    fileName: selectedFile?.name,
    fileSize: selectedFile?.size,
    spotifyUrl,
    trackTitle,
    hasTrackPreview: !!trackPreview
  });
  
  // Validate the request
  if (!validateUploadRequest(spotifyUrl, selectedFile, artistId, validateSpotifyUrl, toast)) {
    console.log("Upload validation failed");
    return { success: false };
  }
  
  // Create basic track details if not provided
  // This bypasses the need to fetch from Spotify API
  const trackId = extractSpotifyTrackId(spotifyUrl);
  if (!trackId) {
    console.error("Failed to extract track ID from URL:", spotifyUrl);
    setUploadError("Couldn't extract track ID from Spotify URL. Please check the URL format.");
    toast({
      title: "Invalid Spotify URL",
      description: "Couldn't extract track ID from URL. Please check the format.",
      variant: "destructive"
    });
    return { success: false };
  }
  
  // Use provided track preview or create minimal track details
  let trackDetails = trackPreview || {
    id: trackId,
    name: trackTitle || "Track " + trackId.substring(0, 6),
    artist: "Unknown Artist",
    albumCover: "",
    spotifyUrl: spotifyUrl
  };
  
  // Override track name with user's title if provided
  if (trackTitle) {
    trackDetails.name = trackTitle;
  }
  
  console.log("Proceeding with upload using track details:", trackDetails);
  
  try {
    console.log("Selected file for upload:", selectedFile?.name);
    
    // Upload file to storage
    if (!selectedFile) {
      console.error("No file selected for upload");
      throw new Error("No file selected");
    }
    
    console.log("Uploading file to storage...");
    const uploadResult = await uploadFileToStorage(spotifyUrl, selectedFile, artistId!);
    if (!uploadResult) {
      console.error("File upload failed");
      throw new Error("Failed to upload file");
    }
    
    if (uploadResult.error) {
      console.error("File upload error:", uploadResult.error);
      setUploadError(uploadResult.error);
      toast({
        title: "Upload error",
        description: uploadResult.error || "Failed to upload file. Please try again.",
        variant: "destructive"
      });
      return { success: false, error: uploadResult.error };
    }
    
    console.log("File uploaded successfully, saving to database...");
    
    // Save to database with track name and artist name
    const newUpload = await saveTrackToDatabase(
      artistId!,
      trackId,
      spotifyUrl,
      uploadResult.publicUrl,
      selectedFile,
      trackDetails,
      trackDescription
    );
    
    if (!newUpload) {
      console.error("Failed to save track to database");
      throw new Error("Failed to save track");
    }
    
    console.log("Track saved to database successfully:", newUpload);
    
    // Display success message with just the track name
    toast({
      title: "Upload successful!",
      description: `"${newUpload.trackName}" has been uploaded.`
    });
    
    return { success: true, upload: newUpload };
    
  } catch (error: any) {
    console.error("Upload error:", error);
    setUploadError(error.message || "Upload failed");
    toast({
      title: "Upload failed",
      description: error.message || "There was an error uploading your file. Please try again.",
      variant: "destructive"
    });
    return { success: false, error };
  }
}
